import React, { useState, useRef } from "react";
import PageInstructions from "@/components/PageInstructions";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import { Share2, Download, Settings, Play } from "lucide-react";
import html2canvas from "html2canvas";

// Mapeamento de dezena para grupo
const dezenaParaGrupo: Record<string, number> = {
  '00': 25, '01': 1, '02': 1, '03': 1, '04': 1,
  '05': 2, '06': 2, '07': 2, '08': 2,
  '09': 3, '10': 3, '11': 3, '12': 3,
  '13': 4, '14': 4, '15': 4, '16': 4,
  '17': 5, '18': 5, '19': 5, '20': 5,
  '21': 6, '22': 6, '23': 6, '24': 6,
  '25': 7, '26': 7, '27': 7, '28': 7,
  '29': 8, '30': 8, '31': 8, '32': 8,
  '33': 9, '34': 9, '35': 9, '36': 9,
  '37': 10, '38': 10, '39': 10, '40': 10,
  '41': 11, '42': 11, '43': 11, '44': 11,
  '45': 12, '46': 12, '47': 12, '48': 12,
  '49': 13, '50': 13, '51': 13, '52': 13,
  '53': 14, '54': 14, '55': 14, '56': 14,
  '57': 15, '58': 15, '59': 15, '60': 15,
  '61': 16, '62': 16, '63': 16, '64': 16,
  '65': 17, '66': 17, '67': 17, '68': 17,
  '69': 18, '70': 18, '71': 18, '72': 18,
  '73': 19, '74': 19, '75': 19, '76': 19,
  '77': 20, '78': 20, '79': 20, '80': 20,
  '81': 21, '82': 21, '83': 21, '84': 21,
  '85': 22, '86': 22, '87': 22, '88': 22,
  '89': 23, '90': 23, '91': 23, '92': 23,
  '93': 24, '94': 24, '95': 24, '96': 24,
  '97': 25, '98': 25, '99': 25
};

const grupoParaNome: Record<number, string> = {
  1: 'Avestruz', 2: 'Águia', 3: 'Burro', 4: 'Borboleta', 5: 'Cachorro',
  6: 'Cabra', 7: 'Carneiro', 8: 'Camelo', 9: 'Cobra', 10: 'Coelho',
  11: 'Cavalo', 12: 'Elefante', 13: 'Galo', 14: 'Gato', 15: 'Jacaré',
  16: 'Leão', 17: 'Macaco', 18: 'Porco', 19: 'Pavão', 20: 'Peru',
  21: 'Touro', 22: 'Tigre', 23: 'Urso', 24: 'Veado', 25: 'Vaca'
};

const tipoTexto: Record<string, string> = {
  'duque-dezena': 'Duque de Dezenas',
  'terno-dezena': 'Terno de Dezenas',
  'duque-grupo': 'Duque de Grupo',
  'terno-grupo': 'Terno de Grupo',
  'quadra-grupo': 'Quadra de Grupos',
  'quina-grupo': 'Quina de Grupo'
};

// Tabela dos 25 grupos com dezenas
const gruposReferencia = [
  { grupo: 1, nome: 'Avestruz', dezenas: '01,02,03,04' },
  { grupo: 2, nome: 'Águia', dezenas: '05,06,07,08' },
  { grupo: 3, nome: 'Burro', dezenas: '09,10,11,12' },
  { grupo: 4, nome: 'Borboleta', dezenas: '13,14,15,16' },
  { grupo: 5, nome: 'Cachorro', dezenas: '17,18,19,20' },
  { grupo: 6, nome: 'Cabra', dezenas: '21,22,23,24' },
  { grupo: 7, nome: 'Carneiro', dezenas: '25,26,27,28' },
  { grupo: 8, nome: 'Camelo', dezenas: '29,30,31,32' },
  { grupo: 9, nome: 'Cobra', dezenas: '33,34,35,36' },
  { grupo: 10, nome: 'Coelho', dezenas: '37,38,39,40' },
  { grupo: 11, nome: 'Cavalo', dezenas: '41,42,43,44' },
  { grupo: 12, nome: 'Elefante', dezenas: '45,46,47,48' },
  { grupo: 13, nome: 'Galo', dezenas: '49,50,51,52' },
  { grupo: 14, nome: 'Gato', dezenas: '53,54,55,56' },
  { grupo: 15, nome: 'Jacaré', dezenas: '57,58,59,60' },
  { grupo: 16, nome: 'Leão', dezenas: '61,62,63,64' },
  { grupo: 17, nome: 'Macaco', dezenas: '65,66,67,68' },
  { grupo: 18, nome: 'Porco', dezenas: '69,70,71,72' },
  { grupo: 19, nome: 'Pavão', dezenas: '73,74,75,76' },
  { grupo: 20, nome: 'Peru', dezenas: '77,78,79,80' },
  { grupo: 21, nome: 'Touro', dezenas: '81,82,83,84' },
  { grupo: 22, nome: 'Tigre', dezenas: '85,86,87,88' },
  { grupo: 23, nome: 'Urso', dezenas: '89,90,91,92' },
  { grupo: 24, nome: 'Veado', dezenas: '93,94,95,96' },
  { grupo: 25, nome: 'Vaca', dezenas: '97,98,99,00' },
];

const PLATFORM_NAME = "IA Palpite Guru";
const PLATFORM_URL = "https://aipalpiteguru.mentepoderosa2030.com/";

const DesdobramentoJB: React.FC = () => {
  const [tipoJogo, setTipoJogo] = useState("duque-dezena");
  const [quantidade, setQuantidade] = useState(3);
  const [inputs, setInputs] = useState<string[]>([]);
  const [showInputs, setShowInputs] = useState(false);
  const [jogosGerados, setJogosGerados] = useState<(string | number)[][]>([]);
  const [showResults, setShowResults] = useState(false);
  const resultsRef = useRef<HTMLDivElement>(null);

  // Reset results and inputs when game type changes to avoid stale data
  const handleTipoJogoChange = (novoTipo: string) => {
    setTipoJogo(novoTipo);
    setShowResults(false);
    setJogosGerados([]);
    setShowInputs(false);
    setInputs([]);
  };

  // Gerar campos de input baseado na configuração
  const gerarInputs = () => {
    let minQtd = 3;
    if (tipoJogo === 'quadra-grupo') minQtd = 4;
    else if (tipoJogo === 'quina-grupo') minQtd = 5;

    if (quantidade < minQtd || quantidade > 20) {
      toast.error(`A quantidade deve estar entre ${minQtd} e 20 para este tipo de jogo`);
      setQuantidade(minQtd);
      return;
    }

    setInputs(new Array(quantidade).fill(''));
    setShowInputs(true);
    setShowResults(false);
  };

  // Atualizar valor de um input específico
  const handleInputChange = (index: number, value: string) => {
    const newInputs = [...inputs];
    newInputs[index] = value;
    setInputs(newInputs);
  };

  // Algoritmo recursivo para gerar combinações
  const gerarCombinacoes = (array: (string | number)[], tamanho: number): (string | number)[][] => {
    const resultado: (string | number)[][] = [];

    const combinar = (inicio: number, combo: (string | number)[]) => {
      if (combo.length === tamanho) {
        resultado.push([...combo]);
        return;
      }

      for (let i = inicio; i < array.length; i++) {
        combo.push(array[i]);
        combinar(i + 1, combo);
        combo.pop();
      }
    };

    combinar(0, []);
    return resultado;
  };

  // Gerar jogos
  const gerarJogos = () => {
    const valores: (string | number)[] = [];
    const isDezena = tipoJogo.includes('dezena');

    for (let i = 0; i < quantidade; i++) {
      const valor = inputs[i]?.trim() || '';

      if (valor === '') {
        toast.error('Preencha todos os campos!');
        return;
      }

      const num = parseInt(valor);

      if (isDezena) {
        if (num < 0 || num > 99 || isNaN(num)) {
          toast.error(`Dezena ${i + 1} deve estar entre 00 e 99`);
          return;
        }
        valores.push(num.toString().padStart(2, '0'));
      } else {
        if (num < 1 || num > 25 || isNaN(num)) {
          toast.error(`Grupo ${i + 1} deve estar entre 01 e 25`);
          return;
        }
        valores.push(num);
      }
    }

    // Verificar duplicatas
    const valoresUnicos = [...new Set(valores.map(v => v.toString()))];
    if (valoresUnicos.length !== valores.length) {
      toast.error('Há valores duplicados! Por favor, insira valores únicos.');
      return;
    }

    let jogos: (string | number)[][] = [];
    const tamanhoCombo: Record<string, number> = {
      'duque-dezena': 2,
      'terno-dezena': 3,
      'duque-grupo': 2,
      'terno-grupo': 3,
      'quadra-grupo': 4,
      'quina-grupo': 5
    };

    jogos = gerarCombinacoes(valores, tamanhoCombo[tipoJogo]);
    setJogosGerados(jogos);
    setShowResults(true);
    toast.success(`${jogos.length} jogos gerados com sucesso!`);
  };

  // Compartilhar resultados
  const handleShare = async () => {
    if (!resultsRef.current) return;

    try {
      const canvas = await html2canvas(resultsRef.current, {
        backgroundColor: '#ffffff',
        scale: 2
      });

      canvas.toBlob(async (blob) => {
        if (!blob) return;

        const shareText = `🎰 ${tipoTexto[tipoJogo]} - Desdobramento JB\n${jogosGerados.length} jogos gerados\n\n🌐 ${PLATFORM_NAME}\n🔗 ${PLATFORM_URL}`;

        if (navigator.share && navigator.canShare) {
          const file = new File([blob], 'desdobramento-jb.png', { type: 'image/png' });
          try {
            await navigator.share({
              title: `Desdobramento JB - ${PLATFORM_NAME}`,
              text: shareText,
              files: [file]
            });
          } catch {
            const url = URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = 'desdobramento-jb.png';
            a.click();
            URL.revokeObjectURL(url);
            toast.success('Imagem baixada com sucesso!');
          }
        } else {
          const url = URL.createObjectURL(blob);
          const a = document.createElement('a');
          a.href = url;
          a.download = 'desdobramento-jb.png';
          a.click();
          URL.revokeObjectURL(url);
          toast.success('Imagem baixada com sucesso!');
        }
      });
    } catch {
      toast.error('Erro ao gerar imagem');
    }
  };

  // Baixar como TXT
  const handleDownload = () => {
    const isDezena = tipoJogo.includes('dezena');
    let content = `${'═'.repeat(45)}\n`;
    content += `   🎰 DESDOBRAMENTO JB - ${tipoTexto[tipoJogo]}\n`;
    content += `   📊 Total de Jogos: ${jogosGerados.length}\n`;
    content += `   🌐 ${PLATFORM_NAME}\n`;
    content += `   🔗 ${PLATFORM_URL}\n`;
    content += `${'═'.repeat(45)}\n\n`;

    jogosGerados.forEach((jogo, index) => {
      if (isDezena) {
        const dezenas = jogo.join(' - ');
        const grupos = jogo.map(d => {
          const grupo = dezenaParaGrupo[d as string];
          return `${grupo.toString().padStart(2, '0')}-${grupoParaNome[grupo]}`;
        });
        content += `${index + 1}. ${dezenas} (${grupos.join(', ')})\n`;
      } else {
        const grupos = jogo.map(g => `${g.toString().padStart(2, '0')}-${grupoParaNome[g as number]}`);
        content += `${index + 1}. ${grupos.join(' | ')}\n`;
      }
    });

    content += `\n${'═'.repeat(45)}\n`;
    content += `   ⚠️ Apenas para fins de entretenimento\n`;
    content += `   Jogue com responsabilidade\n`;
    content += `   🌐 ${PLATFORM_NAME} - ${PLATFORM_URL}\n`;
    content += `${'═'.repeat(45)}\n`;

    const blob = new Blob([content], { type: 'text/plain;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `desdobramento-${tipoJogo}-${new Date().toISOString().slice(0, 10)}.txt`;
    a.click();
    URL.revokeObjectURL(url);
    toast.success('Arquivo TXT baixado!');
  };

  const isDezena = tipoJogo.includes('dezena');

  return (
    <div className="space-y-6">
      <PageInstructions
        title="📖 Como usar o Desdobramento"
        steps={[
          "Escolha quantos grupos deseja desdobrar (2 a 5 grupos)",
          "Selecione os grupos que você quer combinar",
          "Defina o tipo de aposta: dezena, centena ou milhar",
          "Clique em 'Gerar Desdobramento' para ver todas as combinações possíveis",
          "O sistema calcula automaticamente todas as combinações entre os grupos",
          "Veja o custo total da aposta e decida quantas combinações jogar"
        ]}
      />
      {/* Header */}
      <Card className="bg-gradient-to-r from-gray-800 to-black text-white">
        <CardHeader className="text-center">
          <CardTitle className="text-2xl md:text-3xl font-bold flex items-center justify-center gap-2">
            🎰 Sistema de Desdobramento Profissional
          </CardTitle>
          <p className="text-gray-300 text-sm mt-2">
            Jogo do Bicho - Gerador de Combinações
          </p>
        </CardHeader>
      </Card>

      {/* Configuração */}
      <Card className="bg-gray-50">
        <CardContent className="p-6">
          <div className="flex flex-col md:flex-row gap-4 items-end flex-wrap">
            <div className="flex-1 min-w-[200px]">
              <Label className="font-semibold text-gray-700 mb-2 block">Tipo de Jogo:</Label>
              <Select value={tipoJogo} onValueChange={handleTipoJogoChange}>
                <SelectTrigger className="bg-white text-gray-900">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="duque-dezena">Duque de Dezenas</SelectItem>
                  <SelectItem value="terno-dezena">Terno de Dezenas</SelectItem>
                  <SelectItem value="duque-grupo">Duque de Grupo</SelectItem>
                  <SelectItem value="terno-grupo">Terno de Grupo</SelectItem>
                  <SelectItem value="quadra-grupo">Quadra de Grupos</SelectItem>
                  <SelectItem value="quina-grupo">Quina de Grupo</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="min-w-[150px]">
              <Label className="font-semibold text-gray-700 mb-2 block">Quantidade de Números:</Label>
              <Input
                type="number"
                min={3}
                max={20}
                value={quantidade}
                onChange={(e) => setQuantidade(parseInt(e.target.value) || 3)}
                className="bg-white text-gray-900"
              />
            </div>

            <Button
              onClick={gerarInputs}
              className="bg-gradient-to-r from-purple-500 to-indigo-600 hover:from-purple-600 hover:to-indigo-700"
            >
              <Settings className="w-4 h-4 mr-2" />
              Configurar
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Inputs Dinâmicos */}
      {showInputs && (
        <Card>
          <CardHeader>
            <CardTitle className="text-lg text-gray-800">
              {isDezena
                ? `Digite ${quantidade} dezenas (00 a 99):`
                : `Digite ${quantidade} grupos (01 a 25):`}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-4 mb-6">
              {inputs.map((value, index) => (
                <div key={index} className="flex flex-col gap-1">
                  <Label className="text-xs text-gray-600 font-semibold">
                    {isDezena ? `Dezena ${index + 1}` : `Grupo ${index + 1}`}
                  </Label>
                  <Input
                    type="number"
                    min={isDezena ? 0 : 1}
                    max={isDezena ? 99 : 25}
                    placeholder={isDezena ? '00-99' : '01-25'}
                    value={value}
                    onChange={(e) => handleInputChange(index, e.target.value)}
                    className="text-center font-bold text-lg bg-white text-gray-900"
                  />
                </div>
              ))}
            </div>

            <Button
              onClick={gerarJogos}
              className="w-full bg-gradient-to-r from-purple-500 to-indigo-600 hover:from-purple-600 hover:to-indigo-700"
              size="lg"
            >
              <Play className="w-5 h-5 mr-2" />
              Gerar Jogos
            </Button>
          </CardContent>
        </Card>
      )}

      {/* Resultados */}
      {showResults && jogosGerados.length > 0 && (
        <>
          <Card>
            <CardContent className="p-6">
              {/* Área capturada para imagem */}
              <div ref={resultsRef} className="bg-white p-4 rounded-lg">
                {/* Branding Header for image capture */}
                <div className="bg-gradient-to-r from-purple-700 to-indigo-700 text-white p-4 rounded-lg mb-6 text-center">
                  <h2 className="text-xl font-bold">🎰 Desdobramento JB - {tipoTexto[tipoJogo]}</h2>
                  <p className="text-sm opacity-90 mt-1">Gerado por {PLATFORM_NAME}</p>
                  <p className="text-xs opacity-75 mt-0.5">{PLATFORM_URL}</p>
                </div>

                {/* Stats */}
                <div className="bg-blue-50 p-4 rounded-lg mb-6">
                  <div className="flex flex-wrap gap-6">
                    <div className="flex flex-col gap-1">
                      <span className="text-xs text-gray-600 font-semibold">Total de Jogos:</span>
                      <span className="text-2xl font-bold text-purple-600">{jogosGerados.length}</span>
                    </div>
                    <div className="flex flex-col gap-1">
                      <span className="text-xs text-gray-600 font-semibold">Tipo:</span>
                      <span className="text-lg font-bold text-purple-600">{tipoTexto[tipoJogo]}</span>
                    </div>
                  </div>
                </div>

                <h3 className="text-lg font-bold text-gray-800 mb-4">Jogos Gerados:</h3>

                <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-3 mb-6">
                  {jogosGerados.map((jogo, index) => (
                    <div
                      key={index}
                      className={`p-3 rounded-lg border-l-4 text-center font-semibold text-sm ${
                        isDezena
                          ? 'bg-gray-50 border-purple-500'
                          : 'bg-gray-50 border-indigo-600'
                      }`}
                    >
                      {isDezena ? (
                        <>
                          <div className="font-bold text-gray-900">{jogo.join(' - ')}</div>
                          <div className="text-xs text-gray-500 mt-1">
                            {jogo.map(d => {
                              const grupo = dezenaParaGrupo[d as string];
                              return `${grupo}-${grupoParaNome[grupo]}`;
                            }).join(', ')}
                          </div>
                        </>
                      ) : (
                        <div className="text-gray-900">
                          {jogo.map(g => (
                            <div key={String(g)} className="text-xs">
                              {String(g).padStart(2, '0')}-{grupoParaNome[g as number]}
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  ))}
                </div>

                {/* Branding Footer for image capture */}
                <div className="bg-gray-100 p-3 rounded-lg text-center border border-gray-200">
                  <p className="text-xs text-gray-600">
                    ⚠️ Apenas para fins de entretenimento • Jogue com responsabilidade
                  </p>
                  <p className="text-xs text-purple-700 font-semibold mt-1">
                    🌐 {PLATFORM_NAME} — {PLATFORM_URL}
                  </p>
                </div>
              </div>

              {/* Botões de ação - FORA da área de captura */}
              <div className="flex flex-wrap gap-3 mt-4">
                <Button onClick={handleShare} variant="outline" className="flex-1 min-w-[140px]">
                  <Share2 className="w-4 h-4 mr-2" />
                  Compartilhar
                </Button>
                <Button onClick={handleDownload} variant="outline" className="flex-1 min-w-[140px]">
                  <Download className="w-4 h-4 mr-2" />
                  Baixar TXT
                </Button>
              </div>
            </CardContent>
          </Card>
        </>
      )}

      {/* Tabela de Inversões */}
      <Card className="bg-cyan-50 border-2 border-cyan-600">
        <CardHeader>
          <CardTitle className="text-cyan-800 flex items-center gap-2">
            📊 Tabela de Inversões - Total de Combinações
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="overflow-x-auto">
            <table className="w-full border-collapse bg-white rounded-lg overflow-hidden">
              <thead>
                <tr className="bg-cyan-600 text-white">
                  <th className="p-1 sm:p-2 md:p-3 text-left border border-gray-200 text-xs sm:text-sm whitespace-nowrap">Tipo do Jogo</th>
                  {[2, 3, 4, 5, 6, 7, 8, 9, 10].map(n => (
                    <th key={n} className="p-1 sm:p-2 md:p-3 text-center border border-gray-200 text-xs sm:text-sm">{n}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td className="p-1 sm:p-2 md:p-3 border border-gray-200 font-semibold text-xs sm:text-sm whitespace-nowrap">Duque de Grupo/Dezena</td>
                  {[1, 3, 6, 10, 15, 21, 28, 36, 45].map((v, i) => (
                    <td key={i} className="p-1 sm:p-2 md:p-3 text-center border border-gray-200 text-xs sm:text-sm">{v}</td>
                  ))}
                </tr>
                <tr className="bg-gray-50">
                  <td className="p-1 sm:p-2 md:p-3 border border-gray-200 font-semibold text-xs sm:text-sm whitespace-nowrap">Terno de Grupo/Dezena</td>
                  {['-', 1, 4, 10, 20, 35, 56, 84, 120].map((v, i) => (
                    <td key={i} className="p-1 sm:p-2 md:p-3 text-center border border-gray-200 text-xs sm:text-sm">{v}</td>
                  ))}
                </tr>
                <tr>
                  <td className="p-1 sm:p-2 md:p-3 border border-gray-200 font-semibold text-xs sm:text-sm whitespace-nowrap">Quadra de Grupos</td>
                  {['-', '-', 1, 5, 15, 35, 70, 126, 210].map((v, i) => (
                    <td key={i} className="p-1 sm:p-2 md:p-3 text-center border border-gray-200 text-xs sm:text-sm">{v}</td>
                  ))}
                </tr>
                <tr className="bg-gray-50">
                  <td className="p-1 sm:p-2 md:p-3 border border-gray-200 font-semibold text-xs sm:text-sm whitespace-nowrap">Quina de Grupo</td>
                  {['-', '-', '-', 1, 6, 21, 56, 126, 252].map((v, i) => (
                    <td key={i} className="p-1 sm:p-2 md:p-3 text-center border border-gray-200 text-xs sm:text-sm">{v}</td>
                  ))}
                </tr>
              </tbody>
            </table>
          </div>
          <p className="mt-4 text-xs text-cyan-800">
            <strong>Como usar:</strong> Escolha o tipo de jogo e a quantidade de números. A tabela mostra quantas combinações serão geradas.
          </p>
        </CardContent>
      </Card>

      {/* Tabela de Referência - Grupos */}
      <Card className="bg-amber-50">
        <CardHeader>
          <CardTitle className="text-amber-800 flex items-center gap-2">
            📋 Tabela de Referência - Grupos e Dezenas
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-3">
            {gruposReferencia.map((item) => (
              <div
                key={item.grupo}
                className="bg-white p-3 rounded-lg text-center text-sm"
              >
                <div className="font-bold text-purple-600">
                  {item.grupo.toString().padStart(2, '0')}-{item.nome}
                </div>
                <div className="text-gray-600 text-xs mt-1">{item.dezenas}</div>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default DesdobramentoJB;
